import type { PlaylistSegment, Segment, TrackSegment } from "../api/types";

export interface VoiceBlock {
  kind: "voice";
  id: string;
  /** The run-sheet position on the server; null for a block that never came from one. */
  position: number | null;
  title: string;
  audioURL: string;
  duration: number;
  script: string;
}

export interface TrackBlock {
  kind: "track";
  id: string;
  position: number | null;
  provider: "apple_music" | "soundcloud";
  /** What the provider plays: an Apple catalog ID in this storefront, or a SoundCloud URN. */
  playableID: string;
  title: string;
  artist: string;
  artworkURL: string | null;
  duration: number;
  /** Every ID tried for this recording, reported when the server should swap it. */
  candidateIDs: string[];
}

export type Block = VoiceBlock | TrackBlock;

/** A track the listener's storefront can play, as the resolver found it. */
export interface ResolvedTrack {
  playableID: string;
  title: string;
  artist: string;
  duration: number;
  artworkURL: string | null;
}

export type TrackResolver = (segment: TrackSegment) => Promise<ResolvedTrack | null>;

export interface Materialized {
  blocks: Block[];
  /** Tracks this browser cannot play: position and every ID that was tried. */
  rejected: { position: number; ids: string[] }[];
}

/** The id a block gets from its server position, the way the phone names them, so a
 * block always maps back to the position the cursor reports. */
export function blockID(kind: Block["kind"], position: number): string {
  return `radio-${kind}-${position}`;
}

export function candidateIDs(segment: TrackSegment): string[] {
  const ids = [segment.provider_track_id || segment.apple_music_id];
  for (const id of segment.apple_music_fallback_ids ?? []) if (!ids.includes(id)) ids.push(id);
  return ids.filter(Boolean);
}

/**
 * Segments → blocks. A voice introduces the track after it, so a voice whose track
 * cannot play here is dropped with it: no lead-in ever names a song that never comes.
 * A trailing voice with no track after it (news, a sign-off) stands on its own.
 * Port of `RadioRunFollower.materialize` on the phone.
 */
export async function materialize(
  segments: Segment[],
  resolveTrack: TrackResolver,
): Promise<Materialized> {
  const sorted = [...segments].sort((a, b) => a.position - b.position);
  const tracks = sorted.filter((segment): segment is TrackSegment => segment.type === "track");
  const resolved = new Map(
    await Promise.all(
      tracks.map(async (track) => [track.position, await resolveTrack(track)] as const),
    ),
  );

  const blocks: Block[] = [];
  const rejected: Materialized["rejected"] = [];
  let pendingVoice: VoiceBlock | null = null;
  for (const segment of sorted) {
    if (segment.type === "voice") {
      pendingVoice = {
        kind: "voice",
        id: blockID("voice", segment.position),
        position: segment.position,
        title: segment.title,
        audioURL: segment.media_url,
        duration: segment.duration,
        script: segment.script,
      };
      continue;
    }
    const playable = resolved.get(segment.position);
    if (!playable) {
      rejected.push({ position: segment.position, ids: candidateIDs(segment) });
      pendingVoice = null;
      continue;
    }
    if (pendingVoice) {
      blocks.push(pendingVoice);
      pendingVoice = null;
    }
    blocks.push({
      kind: "track",
      id: blockID("track", segment.position),
      position: segment.position,
      provider: segment.music_provider,
      playableID: playable.playableID,
      title: playable.title || segment.title,
      artist: playable.artist || segment.artist,
      artworkURL: segment.artwork_url ?? playable.artworkURL,
      duration: playable.duration || segment.duration,
      candidateIDs: candidateIDs(segment),
    });
  }
  if (pendingVoice) blocks.push(pendingVoice);
  return { blocks, rejected };
}

/** A saved playlist's segments in the run's wire shape, so both play the same way. */
export function segmentsOfPlaylist(segments: PlaylistSegment[]): Segment[] {
  const result: Segment[] = [];
  for (const segment of segments) {
    if (segment.type === "voice") {
      if (!segment.storage_path) continue;
      result.push({
        type: "voice",
        position: segment.position,
        title: segment.title,
        duration: segment.duration,
        media_url: segment.storage_path,
        script: segment.script ?? "",
        voice_profile_id: "",
      });
      continue;
    }
    const provider = segment.music_provider ?? "apple_music";
    const providerTrackID = segment.provider_track_id ?? segment.apple_music_id ?? "";
    if (!providerTrackID) continue;
    result.push({
      type: "track",
      position: segment.position,
      music_provider: provider,
      apple_music_id: segment.apple_music_id ?? "",
      provider_track_id: providerTrackID,
      source_url: segment.source_url,
      isrc: segment.isrc,
      title: segment.title,
      artist: segment.artist ?? "",
      album: "",
      genres: [],
      duration: segment.duration,
      artwork_url: segment.artwork_url,
      apple_music_fallback_ids: segment.apple_music_fallback_ids,
    });
  }
  return result;
}
